import { motion } from 'framer-motion';
import { Award, Book, Users, Target } from 'lucide-react';

const Sobre = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: 'easeOut' },
  };

  const credentials = [
    {
      icon: Award,
      title: 'Formação',
      description: 'Nutricionista CRN RJ 26100311\nProfissional de Educação Física CREF 050596',
    },
    {
      icon: Book,
      title: 'Especializações',
      description: 'Nutrição Esportiva\nTransformação Corporal\nHipertrofia e Emagrecimento',
    },
    {
      icon: Users,
      title: 'Experiência',
      description: 'Mais de 8 anos no mercado de protocolo online',
    },
    {
      icon: Target,
      title: 'Abordagem',
      description: 'Baseada em ciência\nEstratégias personalizadas\nResultados comprováveis',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="sobre-page">
      {/* Hero Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="relative h-[600px] overflow-hidden rounded-lg">
                <img
                  data-editor-key="images.portraitAbout"
                  src="/images/daniel-portrait-2.jpg"
                  alt="Daniel Aron"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-40" />
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.2 }}
            >
              <h1 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
                Daniel Aron
              </h1>
              <p className="text-xl md:text-2xl font-semibold tracking-tight text-[#C0C0C0] mb-8">
                Nutricionista & Personal Trainer
              </p>
              <div className="space-y-6 text-base md:text-lg font-light leading-loose tracking-wide text-[#E5E5E5]">
                <p>
                  Daniel Aron é nutricionista e profissional de educação física, especializado em
                  transformação corporal, emagrecimento e hipertrofia.
                </p>
                <p>
                  Com uma abordagem baseada em ciência e prática de treino, Daniel ajuda seus pacientes a
                  conquistarem resultados reais através de estratégias nutricionais personalizadas e
                  acompanhamento profissional.
                </p>
                <p>
                  Sua metodologia une conhecimento técnico aprofundado com experiência prática, criando
                  protocolos individualizados que respeitam as necessidades, objetivos e estilo de vida de
                  cada cliente.
                </p>
                <p>
                  Ao longo de sua carreira, Daniel já ajudou centenas de pessoas a alcançarem suas metas,
                  seja perda de gordura, ganho de massa muscular ou melhora de performance atlética.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section data-editor-key="credentials" className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Qualificações
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] max-w-2xl mx-auto">
              Experiência e conhecimento para sua transformação
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {credentials.map((item, index) => (
              <motion.div
                key={item.title}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="bg-[#0A0A0A] border border-white/5 p-8 hover:border-white/20 transition-colors"
                data-testid={`credential-${index}`}
              >
                <item.icon className="text-[#C0C0C0] mb-6" size={40} strokeWidth={1.5} />
                <h3 className="text-xl font-semibold tracking-normal mb-4">{item.title}</h3>
                <p className="text-[#A3A3A3] leading-relaxed whitespace-pre-line">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-8">
              Filosofia de Trabalho
            </h2>
            <div className="space-y-6 text-base md:text-lg font-light leading-loose tracking-wide text-[#E5E5E5]">
              <p>
                Acredito que transformação corporal vai além de dietas restritivas e treinos extenuantes. É
                sobre criar um estilo de vida sustentável que gera resultados duradouros.
              </p>
              <p>
                Cada cliente é único. Por isso, desenvolvo protocolos 100% personalizados, considerando
                biotipo, metabolismo, preferências alimentares, rotina e objetivos específicos.
              </p>
              <p>
                Minha abordagem combina nutrição baseada em evidências científicas com prescrição de treino
                inteligente, garantindo resultados máximos com mínimo de sacrifício desnecessário.
              </p>
              <p className="text-[#C0C0C0] font-semibold text-xl">
                "Não existem fórmulas mágicas, mas sim estratégias científicas aplicadas de forma inteligente."
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#121212]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6">
              Vamos começar sua transformação?
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] mb-12">
              Agende uma consulta e descubra como posso ajudar você a alcançar seus objetivos.
            </p>
            <a
              href="https://wa.me/5521967611301"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300"
              data-testid="sobre-cta"
            >
              Agendar consulta
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Sobre;